import { Alert, Button, Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import dayjs, { type Dayjs } from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as API from '../../API';
import type NomenclatureToConsolidate from '../../Models/NomenclatureToConsolidate';
import type OrderToConsolidate from '../../Models/OrderToConsolidate';
import ErrorRow from '../Table/ErrorRow';
import LoadingRow from '../Table/LoadingRow';
import NoRecordsRow from '../Table/NoRecordsRow';
import Styles from './ConsolidateAndResend.module.scss';

function ConsolidateAndResend(): React.ReactElement {
    const { t } = useTranslation();

    const fourMonthsAgo = dayjs().add(-4, 'months');

    const [date, setDate] = useState<Dayjs>(fourMonthsAgo);
    const [includeOnlyFixed, setIncludeOnlyFixed] = useState(true);
    const [orders, setOrders] = useState<OrderToConsolidate[]>([]);
    const [error, setError] = useState(null);
    const [searchRequestRunning, setSearchRequestRunning] = useState(false);
    const [consolidateRequestRunning, setConsolidateRequestRunning] = useState(false);
    const [consolidateResult, setConsolidateResult] = useState(null);

    const noResults = !orders.length;

    const runSearch = (): void => {
        setError(null);
        setSearchRequestRunning(true);
        API.eFact.getBatchesToConsolidate(date.format('YYYY-MM-DD'), includeOnlyFixed)
            .then((res) => {
                console.log('RESULTS', res);
                if (!res.Error) {
                    setOrders(res.Data);
                }
                else {
                    console.error('[ConsolidateAndResend]', res.ErrorData.ExceptionMessage);
                    setOrders([]);
                    setError(res.ErrorData.Message);
                }
            })
            .catch((e) => console.error(e))
            .finally(() => setSearchRequestRunning(false));
    };

    const startConsolidate = (): void => {
        setConsolidateResult(null);
        setConsolidateRequestRunning(true);
        API.eFact.consolidateAndResendEFactBatches(date.format('YYYY-MM-DD'), includeOnlyFixed)
            .then((res) => {
                console.log('Result Consolidate', res);
                setConsolidateResult(res.Data.Message);
                if (res.Error) {
                    console.error('[ConsolidateAndResend]', res.ErrorData.ExceptionMessage);
                    setError(res.ErrorData.Message);
                }
            })
            .catch((e) => console.error(e))
            .finally(() => setConsolidateRequestRunning(false));
    };

    return (
        <div id={Styles.main}>
            <div id={Styles.filter}>
                <div>
                    <div>{t('BatchCreatedBefore')}:</div>
                    <input type="date" value={date.format('YYYY-MM-DD')} onChange={(e) => setDate(dayjs(e.target.value))} />
                </div>
                <div>
                    <FormGroup>
                        <FormControlLabel control={<Checkbox checked={includeOnlyFixed} onChange={(e) => setIncludeOnlyFixed(e.target.checked)} />} label="Include only fixed" />
                    </FormGroup>
                </div>
                <Button size="small" onClick={runSearch} disabled={consolidateRequestRunning || searchRequestRunning} variant="contained">{t('Search')}</Button>
                <Button size="small" onClick={startConsolidate} disabled={noResults || consolidateRequestRunning || searchRequestRunning} variant="contained">Start Consolidation</Button>
                {consolidateResult && <Alert severity="info">{consolidateResult}</Alert>}
            </div>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell />
                            <TableCell sx={{ fontWeight: 'bold' }}>{t('OrderNumber')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>{t('InvoiceID')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>{t('IO')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>{t('Date')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <ConsolidationBody error={error} searchRequestRunning={searchRequestRunning} noResults={noResults} orders={orders} />
                </Table>
            </TableContainer>
        </div>
    );
}

function ConsolidationBody({ error, searchRequestRunning, noResults, orders }: Readonly<{
    error: string | null; searchRequestRunning: boolean; noResults: boolean;
    orders: OrderToConsolidate[];
}>): React.ReactElement {
    return (
        <TableBody>
            {error
                ? <ErrorRow error={error} colSpan={5} />
                : <ConsolidationRecordRows searchRequestRunning={searchRequestRunning} noResults={noResults} orders={orders} />}
        </TableBody>
    );
}

function ConsolidationRecordRows({ searchRequestRunning, noResults, orders }: Readonly<{
    searchRequestRunning: boolean; noResults: boolean; orders: OrderToConsolidate[];
}>): React.ReactElement {
    return (
        <>
            {searchRequestRunning && <LoadingRow colSpan={5} />}
            {noResults && <NoRecordsRow colSpan={5} />}
            {!searchRequestRunning && !noResults && orders.map((x) => <OrderRow key={x.OrderID} order={x} />)}
        </>
    );
}

function OrderRow({ order }: Readonly<{ order: OrderToConsolidate }>): React.ReactElement {
    const { t } = useTranslation();
    const [showMore, setShowMore] = useState(false);

    // Yuck... This uses functionality from RIS javascript to keep pagination(?) working
    const goToOrderClickHandler = (): void => {
        void window.useFilingCabinet(`/Orders/Detail/${order.OrderID}`); 
    };
    const goToInvoice = (): void => {
        void window.useFilingCabinet(`/Orders/Detail/${order.InvoiceID}?invoiceTab=true`); 
    };

    return (
        <>
            <TableRow>
                <TableCell>
                    <IconButton aria-label="expand row" size="small" onClick={() => setShowMore(!showMore)}>
                        {showMore ? <span className="material-symbols-outlined">keyboard_arrow_up</span> : <span className="material-symbols-outlined">keyboard_arrow_down</span>}
                    </IconButton>
                </TableCell>
                <TableCell>
                    <Button onClick={goToOrderClickHandler}>{order.OrderNumber}</Button>
                </TableCell>
                <TableCell>
                    <Button onClick={goToInvoice}>{order.InvoiceID}</Button>
                </TableCell>
                <TableCell>{order.IO}</TableCell>
                <TableCell>{new Date(order.OrderDate).toLocaleDateString('en-GB')}</TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                    <Collapse in={showMore} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1 }}>
                            <Typography variant="h6" gutterBottom component="div">
                                Nomenclatures
                            </Typography>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell />
                                        <TableCell sx={{ fontWeight: 'bold' }}>{t('NomenclatureNumber')}</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>{t('BatchID')}</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>{t('BatchNumber')}</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>{t('Status')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {order.Nomenclatures.map((x: NomenclatureToConsolidate) => <NomenclatureRow key={x.NomenclatureNumber} nomenclature={x} />)}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

function NomenclatureRow({ nomenclature }: Readonly<{ nomenclature: NomenclatureToConsolidate }>): React.ReactElement {
    const { t } = useTranslation();
    const calcStatus = (): string => {
        if (nomenclature.Fixed) {
            return 'Fixed';
        }
        return 'Unknown';
    };
    const status: string = calcStatus();

    let classes = Styles.nomenclature + ' ';
    if (nomenclature.Fixed) {
        classes += Styles.fixed;
    }

    return (
        <TableRow>
            <TableCell className={classes}>
                {nomenclature.Fixed && <span className="material-symbols-outlined">check_circle</span>}
            </TableCell>
            <TableCell className={classes}>{nomenclature.NomenclatureNumber}</TableCell>
            <TableCell className={classes}>{nomenclature.BatchID}</TableCell>
            <TableCell className={classes}>{nomenclature.BatchNumber}</TableCell>
            <TableCell className={classes}>{t(status)}</TableCell>
        </TableRow>
    );
}

export default ConsolidateAndResend;
